import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { Account, FieldDef, PropertyType } from 'app/core/models/account.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Pipeline, Project, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { RelatedToType } from 'app/shared/enums';
import { getAllowedFields } from 'app/shared/functions';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import * as CustomValidator from '../../../../shared/directives/custom-validators.directive';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { toSignal } from '@angular/core/rxjs-interop';
import { TsRxjsOperators } from '@shared/Utils';
import { EMPTY } from 'rxjs';
import { TsSnackbarService } from 'tsui';
import GraphqlService from '@services/graphql.service';

@Component({
  selector: 'property-deal-add',
  templateUrl: './deal-add.component.html',
  styleUrls: ['./deal-add.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PropertyDealAddComponent extends OnDestroyMixin implements OnInit {
  addProjectForm: UntypedFormGroup;
  addProjectByUrlForm: UntypedFormGroup;
  fieldDefs: FieldDef[] = [];
  project: Project;
  pipelines: Pipeline[];
  displayColumns: any[];
  options: [];
  propertyTypes: PropertyType[];
  projectTemplates: ProjectTemplate<any>[];
  isDealsDetailView = true;
  duplicateProjectId = 0;
  duplicateProjects: Project[] = [];
  duplicateAddressProjects: Project[] = [];
  selectedProperties: Property[] = [];
  dealType: 'single' | 'splitPortfolio' | 'mergeIntoPortfolio' | 'portfolio' = 'single';
  creatingDeal = false;
  updatePastPortfolio: Project;
  account: Account;
  linkDealForm = this.formBuilder.group({
    id: [null, Validators.required],
    name: ['', [Validators.required]],
  });
  dealOptions = toSignal(
    this.linkDealForm.get('name').valueChanges.pipe(
      TsRxjsOperators.memoizedDebounceTime(300),
      switchMap((value) => {
        return this.graphqlService.query<Project>(ProjectsService.getDealOptionsWithChildrenGraphqlQuery(value));
      }),
      map((res) => res.edges.map(({ node }) => ({ id: node.id, name: node.name }))),
    ),
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public readonly matDialogRef: MatDialogRef<PropertyDealAddComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly router: Router,
    private readonly projectsService: ProjectsService,
    private readonly accountService: AccountService,
    private readonly snackbarService: TsSnackbarService,
    private readonly graphqlService: GraphqlService,
  ) {
    super();

    this.accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.account = accountData;
    });
  }

  ngOnInit(): void {
    this.addProjectForm = this.formBuilder.group({
      title: ['', [Validators.required, CustomValidator.noWhiteValidator]],
      property_type_id: [''],
      project_type_id: [''],
      pipeline_id: ['', [Validators.required]],
      object_type: ['Deal'],
      template_id: ['', [Validators.required]],
      child_template_id: [''],
    });

    this.addProjectForm
      .get('property_type_id')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((propertyTypeId: any) => {
        const index = this.propertyTypes.findIndex((pType: PropertyType) => pType.id === propertyTypeId);
        let propertyType: PropertyType;
        if (index > -1 && (propertyType = this.propertyTypes[index])) {
          const foundTemplate = this.projectTemplates.find((template: ProjectTemplate<any>) =>
            template.name.toLowerCase().includes(propertyType.name.toLowerCase()),
          );

          if (foundTemplate) {
            this.addProjectForm.get('template_id').setValue(foundTemplate.id);
          }
        }
      });

    this.propertyTypes = this.data.data.options.property_types.filter((option) => !option.hidden);
    this.projectTemplates = this.data.data.options.project_templates
      .filter((option) => !option.hidden && option.related_to_type == RelatedToType.Project)
      .sort((a, b) => a.name.localeCompare(b.name));
    this.fieldDefs = getAllowedFields(this.data.data.fieldDefs, RelatedToType.Project);

    if (this.data?.selectedChildProjects) this.selectedProperties = this.data?.selectedChildProjects?.selected;
    else if (this.data?.selectedProperties) this.selectedProperties = this.data?.selectedProperties?.selected;
    else this.selectedProperties = [];
    this.dealType = this.data?.dealType;
    this.updatePastPortfolio = this.data?.updatePastPortfolio;

    if (this.projectTemplates?.length > 0) {
      this.addProjectForm.get('template_id').setValue(this.projectTemplates[0].id);
    }

    this.projectsService.pipelines$
      .pipe(untilComponentDestroyed(this))
      .subscribe(
        (pipelines: Pipeline[]) =>
          (this.pipelines = pipelines.filter((pipeline: Pipeline) => pipeline?.pipeline_type != 'dynamic')),
      );
  }

  goToDeal(project?: Project): void {
    const { id, pipeline_id } = project || this.project;
    this.router.navigateByUrl('/projects/' + pipeline_id + '/' + id);
    this.matDialogRef.close();
  }

  save(): void {
    if (this.data?.mode === 'link') {
      this.linkDeal();
    } else {
      this.addDeal();
    }
  }

  private linkDeal() {
    this.linkDealForm.disable();

    const property: Property = this.data.selectedProperties.selected[0];
    const { id, name: title } = this.linkDealForm.value;

    this.projectsService
      .relatePropertyToProject(property, <Project>{ id, title })
      .pipe(
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbarService,
          `Deal ${title} linked successfully`,
          'Failed to link deal',
        ),
        take(1),
        catchError(() => {
          this.linkDealForm.enable();
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.matDialogRef.close({ linked_project_id: id });
      });
  }

  private addDeal() {
    this.creatingDeal = true;
    this.addProjectForm.disable();

    const project = this.addProjectForm.value;
    const pipeline = this.pipelines.find((pipeline: Pipeline) => project.pipeline_id == pipeline.id);

    if (pipeline?.table_definition) {
      this.displayColumns = pipeline?.table_definition.fields;
      this.displayColumns = this.displayColumns.filter((column) => column !== 'project_stage_id');
    }

    const selectFields = 'id,title,pipeline_id';

    if (this.dealType == 'single') {
      project.property_ids = this.selectedProperties.map(({ id }: Property) => id);

      this.projectsService
        .saveProject(project)
        .pipe(untilComponentDestroyed(this))
        .subscribe((proj: Project) => {
          this.options = this.data.data;
          this.project = proj;
          this.isDealsDetailView = false;
          this.discard();
          this.matDialogRef.close({ project: proj });
          this.goToDeal();
        });
    } else if (this.dealType == 'portfolio') {
      let payload: Project;
      payload = {
        ...project,
        children: this.selectedProperties.map(({ id, name, address_string }: Property) => ({
          title: (name || address_string || 'Untitled').trim(),
          property_ids: [Number(id)],
          template_id: project.child_template_id ? project.child_template_id : project.template_id,
        })),
        child_ids: this.data.child_ids,
      };
      this.projectsService
        .createPortfolio(payload, selectFields)
        .pipe(untilComponentDestroyed(this))
        .subscribe((portfolio: Project) => {
          this.project = portfolio;
          this.matDialogRef.close({ project: portfolio });
          this.discard();
          this.goToDeal();
        });
    } else if (this.dealType == 'mergeIntoPortfolio') {
      let payload: Project;
      payload = {
        ...project,
        child_ids: this.data.child_ids,
      };
      this.projectsService
        .saveProject(payload)
        .pipe(untilComponentDestroyed(this))
        .subscribe((portfolio: Project) => {
          this.project = portfolio;
          this.matDialogRef.close({ success: true });
        });
    } else if (this.dealType == 'splitPortfolio') {
      if (this.updatePastPortfolio) {
        this.projectsService
          .saveProject(this.updatePastPortfolio)
          .pipe(
            untilComponentDestroyed(this),
            switchMap(() => {
              project.child_ids = this.selectedProperties.map((project) => project.id);
              return this.projectsService.saveProject(project);
            }),
          )
          .subscribe((portfolio: Project) => {
            this.project = portfolio;
            this.discard();
            this.matDialogRef.close({ project: portfolio });
            this.goToDeal();
          });
      }
    }
  }

  discard(): void {
    this.matDialogRef.close();
  }

  updated(): void {
    this.projectsService.saveProject(this.project).pipe(untilComponentDestroyed(this)).subscribe();
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef) => fieldDef.name == name);
  }

  autocompleteSelected(event: MatAutocompleteSelectedEvent) {
    if (event.option.value !== 'No results found!') {
      this.linkDealForm.patchValue({
        id: Number(event.option.id),
        name: event.option.value,
      });
    }
  }
}
